import { chakra } from '@chakra-ui/react';
import DiagonalLineSharpIcon from '@gamma/ui/icons/DiagonalLineSharpIcon';
import PublicSharpIcon from '@gamma/ui/icons/PublicSharpIcon';
import { ComponentPropsWithoutRef, forwardRef } from 'react';

const icons = {
  ip: forwardRef<
    SVGSVGElement,
    ComponentPropsWithoutRef<typeof PublicSharpIcon>
  >(({ size, ...rest }, ref) => {
    const width = {
      xs: '0.875rem',
      sm: 4,
      md: 5,
      lg: 6,
    }[size || 'md'];
    const props = {
      ...rest,
      minWidth: width,
      width,
      maxWidth: width,
      minHeight: width,
      height: width,
      maxHeight: width,
      fill: rest.fill || 'currentColor',
    } as ComponentPropsWithoutRef<(typeof chakra)['svg']>;
    return (
      <chakra.svg
        width={48}
        height={48}
        viewBox="0 0 24 24"
        ref={ref}
        {...props}
      >
        <path
          d="M10.943 21v-2.115l-.096-.015a7.024 7.024 0 0 1-5.717-5.718l-.016-.094H3v-2.116h2.114l.016-.094a7.025 7.025 0 0 1 5.717-5.718l.096-.015V3h2.113v2.115l.096.015a7.028 7.028 0 0 1 5.718 5.718l.015.094H21v2.116h-2.115l-.015.094a7.027 7.027 0 0 1-5.718 5.718l-.096.015V21h-2.113Zm1.056-14.399A5.399 5.399 0 1 0 17.4 12 5.405 5.405 0 0 0 12 6.601Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.717 8.615c-.931 0-1.591.06-2.063.142v6.657h1.512V13c.141.02.32.03.52.03.901 0 1.672-.22 2.193-.713.4-.381.621-.94.621-1.602 0-.66-.292-1.22-.72-1.56-.451-.361-1.123-.54-2.063-.54Zm-.042 3.243c-.22 0-.38-.01-.51-.04V9.827c.11-.03.32-.06.632-.06.76 0 1.191.37 1.191.99 0 .69-.501 1.1-1.313 1.1Z"
          fill="currentColor"
        />
        <path d="M8.8 8.665h1.53v6.748H8.8V8.665Z" fill="currentColor" />
      </chakra.svg>
    );
  }),
  cloud: forwardRef<
    SVGSVGElement,
    ComponentPropsWithoutRef<typeof PublicSharpIcon>
  >(({ size, ...rest }, ref) => {
    const width = {
      xs: '0.875rem',
      sm: 4,
      md: 5,
      lg: 6,
    }[size || 'md'];
    const props = {
      ...rest,
      minWidth: width,
      width,
      maxWidth: width,
      minHeight: width,
      height: width,
      maxHeight: width,
      fill: rest.fill || 'currentColor',
    } as ComponentPropsWithoutRef<(typeof chakra)['svg']>;
    return (
      <chakra.svg
        width={48}
        height={48}
        viewBox="0 0 24 24"
        ref={ref}
        {...props}
      >
        <path
          d="M6 18h2v2H6a6 6 0 0 1-6-6c0-3.1 2.34-5.64 5.35-5.97A7.506 7.506 0 0 1 12 4a7.49 7.49 0 0 1 7.35 6.03c2.6.19 4.65 2.33 4.65 4.97a5 5 0 0 1-2 4v-4a3 3 0 0 0-3-3h-1.5v-.5A5.5 5.5 0 0 0 12 6c-2.5 0-4.63 1.69-5.29 4H6a4 4 0 1 0 0 8Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M20.333 14H9.667a.666.666 0 0 0-.667.667v2.666a.667.667 0 0 0 .667.667h10.666a.666.666 0 0 0 .667-.667v-2.666a.666.666 0 0 0-.667-.667Zm-10 2.667v-1.334h1.334v1.334h-1.334ZM20.333 19.333H9.667A.667.667 0 0 0 9 20v2.667a.667.667 0 0 0 .667.666h10.666a.667.667 0 0 0 .667-.666V20a.666.666 0 0 0-.667-.667Zm-10 2.667v-1.333h1.334V22h-1.334Z"
          fill="currentColor"
        />
      </chakra.svg>
    );
  }),
  domain: PublicSharpIcon,
  connection: DiagonalLineSharpIcon,
};

export const EntityTypeIcon = forwardRef<
  SVGSVGElement,
  Omit<ComponentPropsWithoutRef<typeof PublicSharpIcon>, 'type'> & {
    type: (string & {}) | keyof typeof icons;
  }
>(({ type, ...rest }, ref) => {
  const Icon =
    (type in icons ? icons[type as keyof typeof icons] : undefined) || icons.ip;

  return <Icon data-testid={`entity-type-icon-${type}`} {...rest} ref={ref} />;
});
